import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

interface ActiveScan {
  id: number;
  name: string;
  progress: number;
  is_agent_scan: boolean;
}

interface ActiveSummary {
  server_count: number;
  agent_count: number;
  scans: ActiveScan[];
}

// RunningScansIndicator shows, next to the theme toggle, how many server-side and agent
// scans are currently running. Hovering reveals each running scan's title and progress.
const RunningScansIndicator: React.FC = () => {
  const [data, setData] = useState<ActiveSummary | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/admin/scans/active-summary');
        if (alive) setData(res.data);
      } catch {
        /* non-fatal */
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const total = (data?.server_count || 0) + (data?.agent_count || 0);

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Link
        to="/scans"
        className="theme-toggle"
        title="Laufende Scans"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit', width: 'auto', padding: '0 10px' }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: total ? 1 : 0.45 }}>
          <i className="fas fa-server" style={{ fontSize: '0.85rem', color: 'var(--color-primary-400)' }} />
          <span style={{ fontWeight: 600 }}>{data?.server_count ?? 0}</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: total ? 1 : 0.45 }}>
          <i className="fas fa-robot" style={{ fontSize: '0.85rem', color: 'var(--color-primary-400)' }} />
          <span style={{ fontWeight: 600 }}>{data?.agent_count ?? 0}</span>
        </span>
        {total > 0 && (
          <span
            style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-success-500, #22c55e)', boxShadow: '0 0 0 2px rgba(34,197,94,0.25)' }}
            className="pulse-dot"
          />
        )}
      </Link>

      {open && total > 0 && data && (
        <div
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 1080, minWidth: 280, maxWidth: 360,
            background: 'var(--color-bg-elevated, #1e2538)', border: '1px solid var(--color-border, #2a3348)',
            borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.35)', padding: 10,
          }}
        >
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Laufende Scans · {data.server_count} Server · {data.agent_count} Agent
          </div>
          {data.scans.slice(0, 8).map((s) => (
            <Link
              key={s.id}
              to={`/scans/${s.id}`}
              style={{ display: 'block', padding: '6px 4px', textDecoration: 'none', color: 'var(--color-text-primary)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                <i className={`fas ${s.is_agent_scan ? 'fa-robot' : 'fa-server'}`} style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', width: 14 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name || `Scan #${s.id}`}</span>
                <span style={{ fontWeight: 600, color: 'var(--color-primary-400)' }}>{s.progress}%</span>
              </div>
              <div style={{ height: 4, borderRadius: 3, background: 'var(--color-bg-subtle, #151b2b)', marginTop: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, Math.max(0, s.progress))}%`, background: 'var(--color-primary-500, #6366f1)' }} />
              </div>
            </Link>
          ))}
          {data.scans.length > 8 && (
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginTop: 4 }}>+{data.scans.length - 8} weitere…</div>
          )}
        </div>
      )}
    </div>
  );
};

export default RunningScansIndicator;
